import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/hooks/useAuth';

const TOUR_STORAGE_KEY = 'onboarding_tour_completed';
const TOUR_VERSION = '1'; // Increment to re-show tour after major updates

interface OnboardingTourState {
  hasCompletedTour: boolean;
  isFirstVisit: boolean;
  completeTour: () => void;
  resetTour: () => void;
  shouldShowTour: boolean;
}

export const useOnboardingTour = (): OnboardingTourState => {
  const { user } = useAuth();
  const [hasCompletedTour, setHasCompletedTour] = useState(true); // Default to true to prevent flash
  const [isFirstVisit, setIsFirstVisit] = useState(false);

  useEffect(() => {
    if (!user) return;

    const storageKey = `${TOUR_STORAGE_KEY}_${user.id}_v${TOUR_VERSION}`;
    const completed = localStorage.getItem(storageKey);
    
    if (completed === null) {
      setIsFirstVisit(true);
      setHasCompletedTour(false);
    } else {
      setHasCompletedTour(completed === 'true');
      setIsFirstVisit(false);
    }
  }, [user]);

  const completeTour = useCallback(() => {
    if (!user) return;
    const storageKey = `${TOUR_STORAGE_KEY}_${user.id}_v${TOUR_VERSION}`;
    localStorage.setItem(storageKey, 'true');
    setHasCompletedTour(true);
    setIsFirstVisit(false);
  }, [user]);

  const resetTour = useCallback(() => {
    if (!user) return;
    const storageKey = `${TOUR_STORAGE_KEY}_${user.id}_v${TOUR_VERSION}`;
    localStorage.removeItem(storageKey);
    setHasCompletedTour(false);
    setIsFirstVisit(true);
  }, [user]);

  const shouldShowTour = !hasCompletedTour && !!user;

  return {
    hasCompletedTour,
    isFirstVisit,
    completeTour,
    resetTour,
    shouldShowTour,
  };
};
